import React, { useState, useEffect } from "react";
import { db, storage } from "../../utils/firebase";
import { collection, getDocs, deleteDoc, doc, setDoc, getDoc, updateDoc } from "firebase/firestore"; // Firestore methods
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import Navbar from "../navbar/Navbar";
import Footer from "../footer/Footer";
import './AdminPosts.css'; // Custom styles
import { Link, useLocation } from 'react-router-dom';

const AdminPosts = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]); // State to store filtered posts
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(null); // Track which post is being edited
  const [editedText, setEditedText] = useState(""); // Store the edited text
  const [originalText, setOriginalText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [filteredChoices, setFilteredChoices] = useState([]); // Store filtered choices
  const [selectedLocation, setSelectedLocation] = useState(null);
  const location = useLocation();

  // List of locations for filtering
  const locations = [
    { name: 'Bacoor Eco-Park' },
    { name: 'Molino Dam' },
    { name: 'Senyong\'s Museum' },
    { name: 'Strike Gymnasium' },
    { name: 'Tulay ng Zapote' },
    { name: 'Prinza Dam' },
    { name: 'Fr. Mariano Gomes Monument' },
    { name: 'Kainan sa Balsa' },
    { name: 'Seafood Terminal' },
    { name: 'Digman Halo-Halo' },
    { name: 'Monument of Love' },
    { name: 'General Edilberto Evangelista Monument' },
    { name: 'Justice Buenaventura A. Ocampo Ancestral House' },
    { name: 'Bahay ni Tisa' },
    { name: 'St. Michael the Archangel Church' },
    { name: 'St. Ezekiel Moreno Park' },
    { name: 'Sto. Niño de Molino Parish Church' },
    { name: 'NOMO' },
    { name: 'SOMO' },
    { name: 'SM City Bacoor' },
    { name: 'SM City Molino' },
    { name: 'R. Antonio\'s' },
    { name: 'Main Square Bacoor' },
    { name: 'RFC Molino Mall' },
    { name: 'Funnside Ningnangan Bacoor' },
    { name: 'Brick Wood PH' },
    { name: 'Cocina sa Garaje Gervasio' },
    { name: 'Cavite State University - Bacoor Campus' },
  ];


  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const postsCollectionRef = collection(db, "posts");
        const snapshot = await getDocs(postsCollectionRef);

        if (!snapshot.empty) {
          const postsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Fetch image URLs for each post
          const postsWithImages = await Promise.all(
            postsData.map(async (post) => {
              try {
                if (post.userId && post.imageUrls && post.imageUrls.length > 0) {
                  const imageUrls = await Promise.all(
                    post.imageUrls.map(async (imageName) => {
                      const imageRef = ref(storage, `posts/${post.userId}/${imageName}`);
                      return await getDownloadURL(imageRef);
                    })
                  );
                  return { ...post, imageUrls };
                }
                return post;
              } catch (imageError) {
                console.error(`Error fetching images for post ${post.id}:`, imageError);
                return post; // Return post without images if image fetch fails
              }
            })
          );

          setPosts(postsWithImages);
          setFilteredPosts(postsWithImages); // Initially, show all posts
        } else {
          console.log("No posts found in the database.");
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const handleDeletePost = async (postId) => {
    try {
      const postRef = doc(db, "posts", postId);
      const postSnap = await getDoc(postRef);

      if (postSnap.exists()) {
        const postData = postSnap.data();

        await setDoc(doc(db, "posts_archive", postId), postData);
        await deleteDoc(postRef);

        setPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId));
        setFilteredPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId)); // Also remove from filtered posts
        console.log(`Post ${postId} archived and deleted.`);
      } else {
        console.error("Post not found in UserPosts.");
      }
    } catch (error) {
      console.error("Error archiving post: ", error);
    }
  };

  const handleEditPost = (postId, text) => {
    setEditMode(postId);
    setEditedText(text); 
  };

  const handleSaveEdit = async (postId) => {
    try {
      const postRef = doc(db, "posts", postId);
      await updateDoc(postRef, { text: editedText });

      // Update the post in the state
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId ? { ...post, text: editedText } : post
        )
      );
      setFilteredPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId ? { ...post, text: editedText } : post
        )
      );

      setEditMode(null); // Disable edit mode after saving
    } catch (error) {
      console.error("Error updating post: ", error);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(null); // Disable edit mode
    setEditedText(originalText); // Revert to the original text
  };

  const handleDeleteImage = async (postId, imageUrl) => {
    try {
      const postRef = doc(db, "posts", postId);
      const postSnap = await getDoc(postRef);
  
      if (postSnap.exists()) {
        const postData = postSnap.data();
  
        // Filter out the image URL to delete from the post
        const updatedImageUrls = postData.imageUrls.filter((url) => url !== imageUrl);
  
        // Update the post's image URLs in Firestore (just removing the URL)
        await updateDoc(postRef, { imageUrls: updatedImageUrls });
  
        // Update the post in state (so the UI reflects the change)
        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postId ? { ...post, imageUrls: updatedImageUrls } : post
          )
        );
  
        setFilteredPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postId ? { ...post, imageUrls: updatedImageUrls } : post
          )
        );
  
        console.log("Image URL deleted successfully from Firestore.");
      }
    } catch (error) {
      console.error("Error deleting image URL: ", error);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);

    // Filter the locations based on the search term
    if (e.target.value === "") {
      setFilteredChoices([]); // Close dropdown when search term is empty
    } else {
      const filtered = locations.filter((location) =>
        location.name.toLowerCase().includes(e.target.value.toLowerCase())
      );
      setFilteredChoices(filtered); // Show dropdown if search term is not empty
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setSearchTerm(location.name); // Set the search term to the selected location
    setFilteredChoices([]); // Clear the dropdown
    filterPostsByLocation(location.name); // Filter posts by the selected location
  };

  const filterPostsByLocation = (location) => {
    const filtered = posts.filter((post) =>
      post.location && post.location.toLowerCase().includes(location.toLowerCase())
    );
    setFilteredPosts(filtered);
  };

  if (loading) return <p>Loading posts...</p>;

  return (
    <div className="admin-page">
      <Navbar />
      <div className="admin-container">
        <div className="admin-sidebar">
          <h2>Admin Panel</h2>
          <Link to="/adminusers" className={`sidebar-button ${location.pathname === '/adminusers' ? 'active' : ''}`}>Users</Link>
          <Link to="/adminposts" className={`sidebar-button ${location.pathname === '/adminposts' ? 'active' : ''}`}>User Posts</Link>
          <Link to="/adminfeedback" className={`sidebar-button ${location.pathname === '/adminfeedback' ? 'active' : ''}`}>User Feedback</Link>
          <Link to="/announcementadmin" className={`sidebar-button ${location.pathname === '/announcementadmin' ? 'active' : ''}`}>Announcements</Link>
          <Link to="/exploreaboutadmin" className={`sidebar-button ${location.pathname === '/exploreaboutadmin' ? 'active' : ''}`}>Explore about Bacoor</Link>
          <h2>Places to Visit</h2>
          <Link to="/bridgesadmin" className={`sidebar-button ${location.pathname === '/bridgesadmin' ? 'active' : ''}`}>Bridges & Dams</Link>
          <Link to="/monumentsadmin" className={`sidebar-button ${location.pathname === '/monumentsadmin' ? 'active' : ''}`}>Monuments & Statues</Link>
          <Link to="/houseadmin" className={`sidebar-button ${location.pathname === '/houseadmin' ? 'active' : ''}`}>Ancestral Houses</Link>
          <Link to="/churchesadmin" className={`sidebar-button ${location.pathname === '/churchesadmin' ? 'active' : ''}`}>Churches & Religious Sites</Link>
          <h2>Things to Do</h2>
          <Link to="/recreationaladmin" className={`sidebar-button ${location.pathname === '/recreationaladmin' ? 'active' : ''}`}>Recreational Activities</Link>
          <Link to="/diningadmin" className={`sidebar-button ${location.pathname === '/diningadmin' ? 'active' : ''}`}>Dining and Culinary</Link>
          <Link to="/shoppingadmin" className={`sidebar-button ${location.pathname === '/shoppingadmin' ? 'active' : ''}`}>Shopping and Market</Link>
          <h2>Archives</h2>
          <Link to="/userarchive" className={`sidebar-button ${location.pathname === '/userarchive' ? 'active' : ''}`}>Users</Link>
          <Link to="/postsarchive" className={`sidebar-button ${location.pathname === '/postsarchive' ? 'active' : ''}`}>Posts</Link>
          <Link to="/feedbackarchive" className={`sidebar-button ${location.pathname === '/feedbackarchive' ? 'active' : ''}`}>Feedback</Link>
          <Link to="/announcementarchive" className={`sidebar-button ${location.pathname === '/announcementarchive' ? 'active' : ''}`}>Announcements</Link>
        </div>
        <div className="admin-content">
        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            <h2>User Posts</h2>
        <div className="search-container">
            <input
              type="text"
              placeholder="Search by location..."
              value={searchTerm}
              onChange={handleSearch}
            />
            <button onClick={() => filterPostsByLocation(searchTerm)}>
              <i className="bi bi-search"></i>
            </button>
            {filteredChoices.length > 0 && (
              <div className="dropdown">
                <ul>
                  {filteredChoices.map((choice, index) => (
                    <li key={index} onClick={() => handleLocationSelect(choice)}>
                      {choice.name}
                    </li>
                  ))}
                </ul>
              </div>
              
            )}
          </div>
        </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>User</th>
                  <th>Text</th>
                  <th>Location</th>
                  <th>Rating</th>
                  <th>Image</th>
                  <th>Date Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPosts.map((post) => (
                  <tr key={post.id}>
                    <td>
                      <img src={post.profileImage} alt={`${post.userName}'s profile`} />
                    </td>
                    <td>{post.userName || "Unknown"}</td>
                    <td>
                      {editMode === post.id ? (
                        <input
                          type="text"
                          value={editedText}
                          onChange={(e) => setEditedText(e.target.value)}
                        />
                      ) : (
                        post.text || "No text provided"
                      )}
                    </td>
                    <td>{post.location || "Unknown"}</td>
                    <td>{post.rating || "N/A"}</td>
                    <td>
                      {post.imageUrls && post.imageUrls.length > 0 ? (
                        post.imageUrls.map((url, index) => (
                          <div key={index} style={{ position: "relative" }}>
                            <img
                              src={url}
                              alt={`Post content ${index + 1}`}
                              style={{ maxWidth: "100px", borderRadius: "8px", margin: "5px" }}
                            />
                            {editMode === post.id && (
                              <button
                                style={{
                                  position: "absolute",
                                  top: "5px",
                                  right: "5px",
                                  backgroundColor: "red",
                                  color: "white",
                                  borderRadius: "50%",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleDeleteImage(post.id, url)}
                              >
                                ✖
                              </button>
                            )}
                          </div>
                        ))
                      ) : (
                        "No images"
                      )}
                    </td>
                    <td>{post.createdAt ? new Date(post.createdAt.seconds * 1000).toLocaleString() : "N/A"}</td>
                    <td>
                      {editMode === post.id ? (
                        <>
                          <button
                            className="save-btn btn btn-success"
                            onClick={() => handleSaveEdit(post.id)}
                          >
                            <i className="bi bi-check"></i>
                          </button>
                          <button
                            className="cancel-btn btn btn-secondary"
                            onClick={handleCancelEdit}
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="edit-btn btn btn-primary"
                            onClick={() => handleEditPost(post.id, post.text)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="delete-btn btn btn-danger"
                            onClick={() => handleDeletePost(post.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminPosts;
